import React, { useState, FC, memo } from 'react';

import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { toast } from '@shared/ui';
import { DEFAULT_ERROR_MESSAGE } from '@shared/config';

import { usePasswordChange } from '@entities/auth';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import PasswordInput from '@zvuk-b2b/react-uikit/ui/PasswordInput';

import ProfileInfo from '../ProfileInfo';
import { TEXTS } from '../texts';

import styles from './index.module.scss';

const ChangePasswordModal: FC<ModalProps[ModalTypes.PROFILE_CHANGE_PASSWORD]> = ({ close }) => {
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');

  const [passwordChangeRequest, passwordChangeResult] = usePasswordChange();

  const onCurrentPasswordChange = (value: string) => {
    setCurrentPassword(value);
  };

  const onNewPasswordChange = (value: string) => {
    setNewPassword(value);
  };

  const onChangePasswordSubmit = async (currentPassword: string, newPassword: string) => {
    try {
      const isSuccess = await passwordChangeRequest({
        currentPassword,
        newPassword,
      });

      if (isSuccess) {
        toast.info(TEXTS.PROFILE_CHANGE_PASSWORD_SUCCESS);

        close?.();
      }
    } catch (e: unknown) {
      toast.error(DEFAULT_ERROR_MESSAGE);
    }
  };

  const handleSubmit = () => {
    onChangePasswordSubmit(currentPassword, newPassword);
  };

  return (
    <ModalScreen
      onClose={close}
      className={styles.modal}
      actionButtons={[
        <Button
          key="button_change_password_submit"
          elementId="button_change_password_submit"
          isLoading={passwordChangeResult.loading}
          isDisabled={currentPassword.length === 0 || newPassword.length === 0}
          type={ButtonType.PRIMARY}
          onClick={handleSubmit}>
          {TEXTS.PROFILE_CHANGE_PASSWORD_BUTTON}
        </Button>,
      ]}>
      <div className={styles.content}>
        <ProfileInfo />

        <PasswordInput
          elementId="change_password_input_current_password"
          value={currentPassword}
          isDisabled={passwordChangeResult.loading}
          error={passwordChangeResult.data.errors?.currentPassword?.[0]}
          containerClassName={styles.passwordInput}
          label={TEXTS.PROFILE_CHANGE_PASSWORD_CURRENT}
          placeholder={TEXTS.PROFILE_CHANGE_PASSWORD_CURRENT}
          onChange={onCurrentPasswordChange}
        />

        <PasswordInput
          elementId="change_password_input_new_password"
          value={newPassword}
          isDisabled={passwordChangeResult.loading}
          error={passwordChangeResult.data.errors?.newPassword?.[0]}
          containerClassName={styles.passwordInput}
          label={TEXTS.PROFILE_CHANGE_PASSWORD_NEW}
          placeholder={TEXTS.PROFILE_CHANGE_PASSWORD_NEW}
          onChange={onNewPasswordChange}
          onEnterPressed={handleSubmit}
        />
      </div>
    </ModalScreen>
  );
};

export default memo(ChangePasswordModal);
